import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { connect } from 'react-redux';
import { change, untouch } from 'redux-form';
import { toPlainObject, isEmpty } from 'lodash';
import { length, required } from 'redux-form-validators';

import {
    NAME_LENGTH,
    FIRSTNAME_LENGTH,
    CONTACT_MESSAGE_LENGTH,
    FILE_ACCEPTED_FORMAT
} from '../../../constants/form';
import {
    FormComponent,
    DateInputFieldComponent,
    EmailInputFieldComponent,
    TextInputFieldComponent,
    SelectFieldComponent,
    TextareaFieldComponent,
    FilesInputFieldComponent,
    LegendFieldComponent
} from '../../../components/form';
import {
    toDate,
    nowTimestampMinusDays,
    toTimestamp,
    stringToMoment
} from '../../../utils/dateUtils';
import { STATIC_ROOT } from '../../../constants';
import SvgComponent from '../../../components/SvgComponent';
import { MOMENTJS_DATE_FORMAT_REVERSED } from '../../../constants/moment';
import { CONTACT_REQUEST_REASON_ID_COMPLAINT } from '../../../constants/index';
import { sendContactRequest, reset } from '../../../services/contactrequest/actions';
import { SuccessInfoComponent } from '../../form/SuccessInfoComponent';
import {
    ALL,
    LOGGED,
    MIN_DAYS,
    MAX_DAYS,
    MAX_MONTH
} from '../../../services/contactrequest/constants';

import './ContactRequestFormComponent.scss';

const FORM_NAME = 'contactrequest-form';

const resetFields = {
    lastname: '',
    firstname: '',
    email: ''
};

const getContactRequestReasonsToOptions = (isAuthed, data = []) =>
    data
        .filter(reason =>
            isAuthed
                ? reason.userLoginState === ALL || reason.userLoginState === LOGGED
                : reason.userLoginState === ALL
        )
        .map(item => ({ label: item.label, value: item.id }));

class ContactRequestFormComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = { isComplaint: false };
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.isAuthed && !nextProps.isAuthed) {
            this.resetFields(FORM_NAME, resetFields);
        }
    }

    submit = data => {
        const { sendContactRequest, reset, isAuthed } = this.props;
        if (isAuthed && this.state.isComplaint) {
            const momentDate = stringToMoment(data.purchaseDate);
            data.purchaseDate = toTimestamp(momentDate);
        }

        sendContactRequest(data, () => {
            setTimeout(() => {
                reset();
            }, 3000);
        });
    };

    getUserInfo() {
        const { isAuthed, initialValues } = this.props;
        if (isAuthed) {
            return initialValues;
        }
    }

    resetFields = (formName, fieldsObj) => {
        Object.keys(fieldsObj).forEach(fieldKey => {
            //reset the field's value
            this.props.change(formName, fieldKey, fieldsObj[fieldKey]);

            //reset the field's error
            this.props.untouch(formName, fieldKey);
        });
    };

    handleOnChangeReason = e => {
        const value = e.target.value;
        this.setState({ isComplaint: value === CONTACT_REQUEST_REASON_ID_COMPLAINT });
    };

    render() {
        const {
            i18n,
            i18nFeatureForm,
            isAuthed,
            requestSubmit,
            errorSubmit,
            successSubmit,
            contactRequestReasonsOptions = [],
            errorMessage
        } = this.props;
        if (!successSubmit) {
            return (
                <FormComponent
                    className="contact-request-form"
                    form={FORM_NAME}
                    classNameInputContainer="grid-2 has-gutter"
                    onSubmit={this.submit}
                    initialValues={this.getUserInfo()}
                    enableReinitialize
                    encType
                    requestSubmit={requestSubmit}
                    errorSubmit={errorSubmit}
                    submitText={i18n.BUTTON_SUBMIT}>
                    <EmailInputFieldComponent
                        name="email"
                        label={i18n.LABEL_EMAIL}
                        placeholder={i18n.PLACEHOLDER_EMAIL}
                        classNameInputContainer="mr50"
                        isRequired
                        readOnly={isAuthed}
                    />
                    <TextInputFieldComponent
                        name="lastname"
                        label={i18n.LABEL_LASTNAME}
                        placeholder={i18n.PLACEHOLDER_LASTNAME}
                        isRequired
                        readOnly={isAuthed}
                        classNameInputContainer="one-half"
                        validate={[
                            required({ msg: i18nFeatureForm.REQUIRED }),
                            length({
                                max: NAME_LENGTH,
                                msg: format(i18nFeatureForm.LASTNAME_SIZE_ERROR, NAME_LENGTH)
                            })
                        ]}
                    />
                    <TextInputFieldComponent
                        name="firstname"
                        label={i18n.LABEL_FIRSTNAME}
                        placeholder={i18n.PLACEHOLDER_FIRSTNAME}
                        isRequired
                        readOnly={isAuthed}
                        classNameInputContainer="one-half"
                        validate={[
                            required({ msg: i18nFeatureForm.REQUIRED }),
                            length({
                                max: FIRSTNAME_LENGTH,
                                msg: format(i18nFeatureForm.FIRSTNAME_SIZE_ERROR, FIRSTNAME_LENGTH)
                            })
                        ]}
                    />
                    <SelectFieldComponent
                        name="requestReasonId"
                        defaultOptionLabel={i18n.DEFAULT_OPTION_LABEL}
                        isRequired
                        options={contactRequestReasonsOptions}
                        classNameInputContainer="one-half"
                        validate={[required({ msg: i18n.SELECT_REQUIRED_MESSAGE })]}
                        onChange={this.handleOnChangeReason}
                    />
                    {isAuthed && this.state.isComplaint && (
                        <LegendFieldComponent classNameInputContainer="alert-additionnal-message reason one-half">
                            <SvgComponent
                                src={`${STATIC_ROOT}/contactrequest/warning.svg`}
                                width="39"
                                height="32"
                            />
                            {i18n.TEXT_WARNING_CASHBACK}
                        </LegendFieldComponent>
                    )}
                    {isAuthed && this.state.isComplaint && (
                        <React.Fragment>
                            <DateInputFieldComponent
                                name="purchaseDate"
                                label={i18n.LABEL_PURCHASE_DATE}
                                classNameInputContainer="one-half"
                                isRequired
                                mustBeDatePast
                                max={toDate(
                                    nowTimestampMinusDays(MIN_DAYS),
                                    MOMENTJS_DATE_FORMAT_REVERSED
                                )}
                                min={toDate(
                                    nowTimestampMinusDays(MAX_DAYS),
                                    MOMENTJS_DATE_FORMAT_REVERSED
                                )}
                                key={0}
                            />
                            <LegendFieldComponent
                                classNameInputContainer="alert-additionnal-message period one-half"
                                key={1}>
                                <SvgComponent
                                    src={`${STATIC_ROOT}/contactrequest/warning.svg`}
                                    width="39"
                                    height="32"
                                />
                                {format(i18n.TEXT_WARNING_REQUEST_PERIOD, MAX_MONTH, MIN_DAYS)}
                            </LegendFieldComponent>
                        </React.Fragment>
                    )}
                    <FilesInputFieldComponent
                        name="files"
                        btText={i18n.BUTTON_CHOOSE_FILE}
                        formatAccepted={FILE_ACCEPTED_FORMAT}
                        maxSize={2}
                        multiple
                        classNameInputContainer="full"
                    />
                    <TextareaFieldComponent
                        name="message"
                        label={i18n.LABEL_MESSAGE}
                        isRequired
                        classNameInputContainer="full"
                        validate={[
                            required({ msg: i18n.TEXT_AREA_REQUIRED_MESSAGE }),
                            length({
                                max: CONTACT_MESSAGE_LENGTH,
                                msg: format(i18n.MESSAGE_SIZE_ERROR, CONTACT_MESSAGE_LENGTH)
                            })
                        ]}
                    />
                    {errorMessage && (
                        <LegendFieldComponent classNameInputContainer="color-err">
                            {errorMessage}
                        </LegendFieldComponent>
                    )}
                </FormComponent>
            );
        } else {
            return (
                <SuccessInfoComponent>
                    <p>{i18n.SUBMIT_SUCCEEDED}</p>
                </SuccessInfoComponent>
            );
        }
    }
}

ContactRequestFormComponent.propTypes = {
    i18n: PropTypes.object.isRequired,
    contactRequestReasonsOptions: PropTypes.array.isRequired
};

function mapStateToProps(state) {
    const user = toPlainObject(state.account.user);

    return {
        errorMessage: state.contactrequest.error,
        requestSubmit: state.contactrequest.fetching || false,
        errorSubmit: !isEmpty(state.contactrequest.errorMessage),
        successSubmit: state.contactrequest.successContactRequestSubmit,
        fetching: state.contactrequest.fetching,
        contactRequestReasonsOptions: getContactRequestReasonsToOptions(
            !isEmpty(state.authentication.token),
            state.contactrequest.contactrequestreasons
        ),
        i18n: state.i18n.page.CONTACTREQUEST,
        i18nFeatureForm: state.i18n.feature.form,
        isAuthed: !isEmpty(state.authentication.token),
        initialValues: {
            lastname: user.lastname ? user.lastname : '',
            firstname: user.firstname ? user.firstname : '',
            email: user.email ? user.email : ''
        }
    };
}

const mapDispatchtoProps = {
    sendContactRequest,
    reset,
    change,
    untouch
};

export default connect(mapStateToProps, mapDispatchtoProps)(ContactRequestFormComponent);



// WEBPACK FOOTER //
// ./src/components/pages/contactrequest/ContactRequestFormComponent.js